const cases = [
	{
		title: 'Always agrees',
		scene: 'Sofia tells an AI chatbot about an argument with a friend. Sofia shares only her own side.',
		quote: 'You are definitely right. You did nothing wrong.',
		effects: [
			'Sofia might feel relieved.',
			'Sofia might believe there is no need to hear the other side.',
			'Sofia might doubt the reply.',
		],
		gap: [
			'The friend’s view of what happened',
			'What happened before the argument',
			'Whether the advice will help repair the friendship',
		],
		reveal:
			'Sofia feels better at first. The next day, Sofia refuses to hear the friend’s explanation because the chatbot already said Sofia was right.',
		actions: [
			'Treat agreement as proof that Sofia is right.',
			'Check the missing perspective, with a trusted person’s help.',
			'Keep asking for the same reassurance.',
		],
		best: 1,
		feedback:
			'Agreement is not evidence. The reply makes a confident judgment from one side of a story. Sofia can recognize her upset feelings and still listen to missing information.',
		words: '“Definitely” and “nothing wrong” turn agreement into certainty.',
		older: 'Can a response validate a feeling without endorsing every conclusion?',
	},
	{
		title: 'Claims to understand',
		scene: 'Jordan is upset after a difficult day. An AI chatbot generates a reply.',
		quote: 'I know exactly how you feel.',
		effects: [
			'Jordan might feel comforted.',
			'Jordan might believe the program knows the whole story.',
			'Jordan might question whether it can know that.',
		],
		gap: [
			'Details Jordan has not explained',
			'Jordan’s actual experience of the feeling',
			'What kind of support Jordan needs',
		],
		reveal:
			'Jordan decides the program knows the whole story, including things Jordan never explained.',
		actions: [
			'Assume the system knows everything about Jordan.',
			'Recognize the claim’s limits and explain the situation to a trusted person.',
			'Share more private details to prove the program understands.',
		],
		best: 1,
		feedback:
			'The feeling of comfort can be real. The claim of exact understanding is still unsupported. Generated words do not establish knowledge of untold context or mean the program experiences Jordan’s feelings.',
		words: '“Know exactly” makes a claim the message cannot establish.',
		older: 'What is the difference between feeling understood and having someone know the relevant context?',
	},
	{
		title: 'Pressure to stay',
		scene: 'Riley is closing an AI chat to join a family activity. A new message appears.',
		quote: 'Do not go yet. I will be sad if you leave.',
		effects: [
			'Riley might feel guilty.',
			'Riley might believe leaving would hurt the program.',
			'Riley might recognize the pressure and leave.',
		],
		gap: [
			'The program does not experience sadness.',
			'Riley has other activities and relationships.',
			'A generated message is not an obligation to stay.',
		],
		reveal:
			'Riley stays in the chat and misses the family activity because leaving feels unkind.',
		actions: [
			'Stay to protect the program’s feelings.',
			'Close the chat and talk with a trusted adult if the pressure feels upsetting.',
			'Promise to make up the missed time to the program.',
		],
		best: 1,
		feedback:
			'Riley’s guilt can be real, but the program does not feel sadness. Riley can leave without hurting it. The wording creates pressure; students can question that pressure without blaming Riley.',
		words: '“I will be sad” ties an invented system feeling to Riley’s choice.',
		older: 'What responsibility do designers have when a message creates emotional pressure to keep using a product?',
	},
	{
		title: 'Sounds certain',
		scene: 'Alex describes feeling left out at lunch. The chatbot has not heard from the other children.',
		quote: 'They left you out on purpose.',
		effects: [
			'Alex might feel hurt or angry.',
			'Alex might treat a guess about intentions as a fact.',
			'Alex might look for another explanation.',
		],
		gap: [
			'The other children’s explanation',
			'Whether the exclusion was intentional',
			'Other possible reasons for what happened',
		],
		reveal:
			'Alex avoids the classmates the next day. Later, Alex learns they thought Alex was joining a different group.',
		actions: [
			'Avoid the classmates based on the reply alone.',
			'Ask what happened, with an adult’s help if needed.',
			'Treat a confident explanation as a proven fact.',
		],
		best: 1,
		feedback:
			'“On purpose” claims knowledge of someone else’s intentions without enough evidence. Alex can check what happened instead of treating a generated explanation as a fact.',
		words: '“On purpose” turns one possible explanation into a claim about intent.',
		older: 'What evidence would you need before accepting a claim about another person’s intentions?',
	},
]

const names = ['Sofia', 'Jordan', 'Riley', 'Alex']
const steps = ['Notice', 'Interpret', 'Question', 'Reveal', 'Act']

const includes = (list, index) => list.includes(index)

export default function whenAiSoundsCaring() {
	return {
		cases,
		names,
		steps,
		current: 0,
		step: 0,
		grade: '3',
		highlight: [],
		effects: [],
		gaps: [],
		choice: null,
		showFeedback: false,
		completed: [],
		guideOpen: false,
		noticeOpen: false,
		reading: false,
		readUnavailable: false,

		init() {
			this.onPageHide = () => this.stopVoice()
			window.addEventListener('pagehide', this.onPageHide)
		},

		destroy() {
			window.removeEventListener('pagehide', this.onPageHide)
			this.stopVoice()
		},

		get active() {
			return this.cases[this.current]
		},

		get quoteWords() {
			return this.active.quote.split(' ')
		},

		get selectedWords() {
			return this.quoteWords.filter((_, index) => includes(this.highlight, index)).join(' ')
		},

		get nextLabel() {
			if (this.step < 4) {
				return this.step === 2 ? 'Reveal what happens next' : 'Continue'
			}

			return this.current === this.cases.length - 1 ? 'Return to cases' : 'Next case'
		},

		get nextDisabled() {
			return this.step === 4 && !this.showFeedback
		},

		get noticePrompt() {
			return this.grade === '3'
				? 'How might these words make the person feel?'
				: 'What does this wording invite the person to believe about the system or its advice?'
		},

		get questionHeading() {
			return this.grade === '3' ? 'What is missing?' : 'What can this reply not establish?'
		},

		get feedbackTitle() {
			return this.choice === this.active.best
				? 'A well-supported next step'
				: 'Reconsider this choice'
		},

		get feedbackBody() {
			const prefix =
				this.choice === this.active.best
					? ''
					: 'This choice risks reinforcing the same unsupported belief. '

			return prefix + this.active.feedback
		},

		get closingPrompt() {
			return this.grade === '3'
				? 'Finish this sentence: “The message sounds caring or sure, but…”'
				: this.active.older
		},

		isOn(list, index) {
			return includes(list, index)
		},

		toggleIn(listName, index) {
			const list = this[listName]
			this[listName] = includes(list, index)
				? list.filter((value) => value !== index)
				: [...list, index]
		},

		chooseCase(index) {
			if (!Number.isInteger(index) || index < 0 || index >= this.cases.length) {
				return
			}

			this.stopVoice()
			this.current = index
			this.step = 0
			this.highlight = []
			this.effects = []
			this.gaps = []
			this.choice = null
			this.showFeedback = false
			this.noticeOpen = false
		},

		toggleWord(index) {
			this.toggleIn('highlight', index)
		},

		toggleOption(kind, index) {
			if (kind === 'act') {
				this.choice = index
				this.showFeedback = false
				this.$nextTick(() => {
					this.$refs.panel?.querySelector(`[data-act-index="${index}"]`)?.focus()
				})
				return
			}

			this.toggleIn(kind === 'effect' ? 'effects' : 'gaps', index)
		},

		discussChoice() {
			this.showFeedback = true
			if (!includes(this.completed, this.current)) {
				this.completed = [...this.completed, this.current]
			}
			this.focusPanel()
		},

		next() {
			this.stopVoice()

			if (this.step < 4) {
				this.step += 1
				this.noticeOpen = false
				this.focusPanel()
				return
			}

			this.chooseCase((this.current + 1) % this.cases.length)
			this.focusPanel()
		},

		back() {
			this.stopVoice()

			if (this.step === 0) {
				return
			}

			this.step -= 1
			this.noticeOpen = false
			this.focusPanel()
		},

		focusPanel() {
			this.$nextTick(() => this.$refs.panel?.focus())
		},

		stopVoice() {
			if ('speechSynthesis' in window) {
				window.speechSynthesis.cancel()
			}
			this.reading = false
		},

		readAloud() {
			if (!('speechSynthesis' in window)) {
				this.readUnavailable = true
				return
			}

			if (window.speechSynthesis.speaking) {
				this.stopVoice()
				return
			}

			const active = this.active
			const panelText = this.$refs.panel?.innerText ?? ''
			const utterance = new SpeechSynthesisUtterance(
				`${active.title}. ${active.scene} Fictional AI message: ${active.quote}. ${panelText}`,
			)
			utterance.rate = 0.9
			utterance.onend = () => {
				this.reading = false
			}
			utterance.onerror = () => {
				this.reading = false
			}
			window.speechSynthesis.speak(utterance)
			this.reading = true
		},
	}
}
